import React, { useEffect, useState } from "react";
import { Modal } from "rsuite";
import { ptBR } from "date-fns/locale";
import DatePicker from "react-datepicker";
import {
  MdCalendarMonth,
  MdEdit,
  MdLock,
  MdWorkOutline,
  MdMailOutline,
  MdChat,
  MdSupportAgent,
} from "react-icons/md";
import { toast } from "react-toastify";
import api from "../../../../services/axios";

// ─── Types ────────────────────────────────────────────────────────────────────

type BillingType = "fixed" | "hourly" | "recurring" | "";

interface ProjectUpdatePayload {
  id: number;
  projectName: string;
  description?: string;
  startDate: Date | undefined;
  estimatedEndDate: Date | undefined;
  billingType: number | null;
  amount: number | null;
  contractedHours: number | null;
  billingChannels: number[];
  acceptsTickets: boolean;
}

interface ProjectDetails {
  id: number;
  projectName: string;
  description?: string;
  startDate?: string;
  estimatedEndDate?: string;
  dtCreated?: string;
  billingType?: number;
  amount?: number | null;
  contractedHours?: number | null;
  billingChannels?: (number | { channel: number })[];
  acceptsTickets?: boolean;
  clientName?: string | null;
}

interface Props {
  openProjectDetailModal: boolean;
  setOpenProjectDetailModal: (open: boolean) => void;
  details: ProjectDetails;
  updateState: () => void;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const BILLING_TYPE_TO_ID: Record<Exclude<BillingType, "">, number> = {
  fixed: 1,
  hourly: 2,
  recurring: 3,
};

const BILLING_ID_TO_TYPE: Record<number, BillingType> = {
  1: "fixed",
  2: "hourly",
  3: "recurring",
};

const BILLING_AMOUNT_LABEL: Record<Exclude<BillingType, "">, string> = {
  fixed: "Valor fixo",
  hourly: "Valor por hora",
  recurring: "Valor mensal",
};

const BILLING_AMOUNT_PLACEHOLDER: Record<Exclude<BillingType, "">, string> = {
  fixed: "0.00",
  hourly: "0.00 / hr",
  recurring: "0.00 / mês",
};

const CHANNEL_EMAIL = 1;
const CHANNEL_WHATSAPP = 2;

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatDate = (iso?: string) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("pt-BR");
};

const resolveChannelId = (c: number | { channel: number }): number =>
  typeof c === "object" ? c.channel : c;

const toastOptions = {
  position: "top-right" as const,
  autoClose: 4000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const labelCls = "text-sm font-semibold text-gray-700 dark:text-gray-300";

const inputClass = (editable: boolean) =>
  `w-full rounded-xl border px-3 py-2.5 text-sm transition-all focus:outline-none ${
    editable
      ? "border-gray-200 bg-white text-gray-800 focus:border-dv-blue-500 focus:ring-2 focus:ring-dv-blue-500/20 dark:border-gray-600 dark:bg-navy-900 dark:text-white"
      : "cursor-not-allowed border-gray-100 bg-gray-50 text-gray-700 dark:border-gray-700 dark:bg-navy-900/50 dark:text-gray-300"
  }`;

const datePickerClass = (editable: boolean) =>
  `h-[42px] w-full rounded-xl border px-3 text-sm transition-all focus:outline-none ${
    editable
      ? "border-gray-200 bg-white text-gray-800 focus:border-dv-blue-500 focus:ring-2 focus:ring-dv-blue-500/20 dark:border-gray-600 dark:bg-navy-900 dark:text-white"
      : "cursor-not-allowed border-gray-100 bg-gray-50 dark:border-gray-700 dark:bg-navy-900/50"
  }`;

// ─── Component ────────────────────────────────────────────────────────────────

const ModalViewDetailProject: React.FC<Props> = ({
  openProjectDetailModal,
  setOpenProjectDetailModal,
  details,
  updateState,
}) => {
  const [projectName, setProjectName] = useState("");
  const [description, setDescription] = useState("");
  const [startDate, setStartDate] = useState<Date | undefined>();
  const [endDate, setEndDate] = useState<Date | undefined>();
  const [billingType, setBillingType] = useState<BillingType>("");
  const [billingAmount, setBillingAmount] = useState("");
  const [contractedHours, setContractedHours] = useState("");
  const [billingEmail, setBillingEmail] = useState(false);
  const [billingWhatsapp, setBillingWhatsapp] = useState(false);
  const [recebeChamados, setRecebeChamados] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  // Reset form whenever the modal opens with new details
  useEffect(() => {
    if (!openProjectDetailModal) return;

    setIsEditing(false);
    setProjectName(details?.projectName ?? "");
    setDescription(details?.description ?? "");
    setStartDate(details?.startDate ? new Date(details.startDate) : undefined);
    setEndDate(details?.estimatedEndDate ? new Date(details.estimatedEndDate) : undefined);
    setBillingType(details?.billingType ? (BILLING_ID_TO_TYPE[details.billingType] ?? "") : "");
    setBillingAmount(details?.amount != null ? String(details.amount) : "");
    setContractedHours(details?.contractedHours != null ? String(details.contractedHours) : "");

    const channels = (details?.billingChannels ?? []).map(resolveChannelId);
    setBillingEmail(channels.includes(CHANNEL_EMAIL));
    setBillingWhatsapp(channels.includes(CHANNEL_WHATSAPP));
    setRecebeChamados(details?.acceptsTickets ?? false);
  }, [openProjectDetailModal, details]);

  const handleClose = () => setOpenProjectDetailModal(false);

  const handleUpdateProject = async () => {
    const billingChannels: number[] = [
      ...(billingEmail ? [CHANNEL_EMAIL] : []),
      ...(billingWhatsapp ? [CHANNEL_WHATSAPP] : []),
    ];

    const payload: ProjectUpdatePayload = {
      id: details.id,
      projectName,
      description,
      startDate,
      estimatedEndDate: endDate,
      billingType: billingType ? BILLING_TYPE_TO_ID[billingType] : null,
      amount: billingAmount !== "" ? parseFloat(billingAmount) : null,
      // O PUT substitui o projeto inteiro: campo que não vai é gravado vazio. Sem isto, salvar apagava as horas contratadas.
      contractedHours: contractedHours !== "" ? parseFloat(contractedHours) : null,
      billingChannels,
      acceptsTickets: recebeChamados,
    };

    try {
      setLoading(true);
      await api.put("/project", payload);
      toast.success("Project updated successfully", toastOptions);
      updateState();
      handleClose();
    } catch (error: any) {
      const message =
        error?.response?.data?.message ?? "Network error updating project";
      toast.error(message, toastOptions);
    } finally {
      setLoading(false);
    }
  };

  const isFormValid =
    isEditing &&
    projectName.trim().length >= 3 &&
    startDate != null &&
    endDate != null;

  return (
    <Modal backdrop="static" open={openProjectDetailModal} size="lg" onClose={handleClose}>
      {/* ── Header ── */}
      <Modal.Header className="border-b border-gray-100 pb-3 dark:border-gray-700">
        <Modal.Title>
          <div className="flex items-center justify-between pr-6">
            <div className="flex items-center gap-3">
              <div className="dv-aura flex h-11 w-11 items-center justify-center rounded-2xl bg-dv-gradient text-white shadow-dv-glow">
                <MdWorkOutline className="h-6 w-6" aria-hidden="true" />
              </div>
              <div>
                <h2 className="text-base font-bold text-navy-700 dark:text-white">
                  Detalhes do projeto
                </h2>
                <p className="text-xs font-normal text-gray-700 dark:text-gray-300">
                  {details?.projectName || "—"}
                </p>
              </div>
            </div>

            <button
              type="button"
              aria-label={isEditing ? "Bloquear edição" : "Editar projeto"}
              onClick={() => setIsEditing((prev) => !prev)}
              className={`flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-xs font-semibold transition-all ${
                isEditing
                  ? "bg-amber-100 text-amber-700 hover:bg-amber-200 dark:bg-amber-900/40 dark:text-amber-300"
                  : "bg-dv-blue-500/10 text-dv-blue-600 hover:bg-dv-blue-500/20 dark:text-dv-cyan-300"
              }`}
            >
              {isEditing ? (
                <><MdLock className="h-3.5 w-3.5" aria-hidden="true" /> Bloquear</>
              ) : (
                <><MdEdit className="h-3.5 w-3.5" aria-hidden="true" /> Editar</>
              )}
            </button>
          </div>
        </Modal.Title>
      </Modal.Header>

      {/* ── Body ── */}
      <Modal.Body>
        <div className="flex flex-col gap-4 px-1 py-4">

          {/* Row 1 – Project Name & Created Date */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="view-project-name" className={labelCls}>
                Nome do projeto <span className="text-red-500">*</span>
              </label>
              <input
                id="view-project-name"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                disabled={!isEditing}
                className={inputClass(isEditing)}
                placeholder="ex.: Redesign do site"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="view-project-created" className={labelCls}>
                Data de criação
              </label>
              <input
                id="view-project-created"
                disabled
                value={formatDate(details?.dtCreated)}
                className={`${inputClass(false)} tabular-nums`}
              />
            </div>
          </div>

          {/* Row 2 – Dates */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {(
              [
                { id: "view-project-startDate", label: "Data de início", value: startDate, onChange: setStartDate },
                { id: "view-project-endDate", label: "Término previsto", value: endDate, onChange: setEndDate },
              ] as const
            ).map(({ id, label, value, onChange }) => (
              <div key={id} className="flex flex-col gap-1.5">
                <label htmlFor={id} className={labelCls}>
                  {label} <span className="text-red-500">*</span>
                </label>
                <DatePicker
                  id={id}
                  showIcon
                  selected={value}
                  locale={ptBR}
                  icon={<MdCalendarMonth className="mt-1.5 text-base" aria-hidden="true" />}
                  dateFormat="dd-MM-yyyy"
                  onChange={(date) => onChange(date ? new Date(date) : undefined)}
                  disabled={!isEditing}
                  placeholderText={`Selecione ${label.toLowerCase()}`}
                  className={datePickerClass(isEditing)}
                />
              </div>
            ))}
          </div>

          {/* Description */}
          <div className="flex flex-col gap-1.5">
            <label htmlFor="view-project-description" className={labelCls}>
              Descrição
            </label>
            <textarea
              id="view-project-description"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={!isEditing}
              placeholder="Breve descrição do projeto (opcional)"
              className={`${inputClass(isEditing)} resize-none`}
            />
          </div>

          {/* Divider */}
          <div className="flex items-center gap-3">
            <hr className="flex-1 border-gray-100 dark:border-gray-700" />
            <span className="text-xs font-semibold uppercase tracking-wider text-gray-700 dark:text-gray-300">
              Faturamento
            </span>
            <hr className="flex-1 border-gray-100 dark:border-gray-700" />
          </div>

          {/* Row 3 – Billing Type & Amount */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="view-project-billingType" className={labelCls}>
                Tipo de faturamento
              </label>
              <select
                id="view-project-billingType"
                value={billingType}
                onChange={(e) => setBillingType(e.target.value as BillingType)}
                disabled={!isEditing}
                className={inputClass(isEditing)}
              >
                <option value="">Selecione o tipo</option>
                <option value="fixed">Fixed Price</option>
                <option value="hourly">Hourly Rate</option>
                <option value="recurring">Recurring</option>
              </select>
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="view-project-amount" className={labelCls}>
                {billingType ? BILLING_AMOUNT_LABEL[billingType] : "Valor"}
              </label>
              <div className="relative">
                <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm text-gray-400" aria-hidden="true">
                  R$
                </span>
                <input
                  id="view-project-amount"
                  type="number"
                  min="0"
                  step="0.01"
                  value={billingAmount}
                  onChange={(e) => setBillingAmount(e.target.value)}
                  disabled={!isEditing || !billingType}
                  placeholder={billingType ? BILLING_AMOUNT_PLACEHOLDER[billingType] : "0.00"}
                  className={`w-full rounded-xl border py-2.5 pl-9 pr-3 text-sm transition-all focus:outline-none ${
                    isEditing && billingType
                      ? "border-gray-200 bg-white text-gray-800 focus:border-dv-blue-500 focus:ring-2 focus:ring-dv-blue-500/20 dark:border-gray-600 dark:bg-navy-900 dark:text-white"
                      : "cursor-not-allowed border-gray-100 bg-gray-50 text-gray-700 opacity-60 dark:border-gray-700 dark:bg-navy-900/50 dark:text-gray-300"
                  }`}
                />
              </div>
            </div>
          </div>

          {/* Row 3b – Contracted Hours */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="view-project-contractedHours" className={labelCls}>
                Horas contratadas
              </label>
              <input
                id="view-project-contractedHours"
                type="number"
                min="0"
                step="0.5"
                value={contractedHours}
                onChange={(e) => setContractedHours(e.target.value)}
                disabled={!isEditing}
                placeholder="Sem limite"
                className={inputClass(isEditing)}
              />
            </div>
          </div>

          {/* Row 4 – Billing Channels */}
          <div className="flex flex-col gap-2">
            <span className={labelCls}>Canal de faturamento</span>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              {/* Email */}
              <ChannelCheckbox
                checked={billingEmail}
                onChange={setBillingEmail}
                disabled={!isEditing}
                label="E-mail"
                icon={<MdMailOutline className="h-4 w-4" aria-hidden="true" />}
              />
              {/* WhatsApp */}
              <ChannelCheckbox
                checked={billingWhatsapp}
                onChange={setBillingWhatsapp}
                disabled={!isEditing}
                label="WhatsApp"
                icon={<MdChat className="h-4 w-4" aria-hidden="true" />}
              />
            </div>
          </div>

          {/* Row 5 – Chamados */}
          <div className="flex flex-col gap-2">
            <span className={labelCls}>Chamados</span>
            <div>
              <ChannelCheckbox
                checked={recebeChamados}
                onChange={setRecebeChamados}
                disabled={!isEditing}
                label="Recebe os chamados do cliente"
                icon={<MdSupportAgent className="h-4 w-4" aria-hidden="true" />}
              />
            </div>
            <p className="text-xs text-gray-600 dark:text-gray-400">
              Os chamados novos{details?.clientName && details.clientName !== "—" ? ` de ${details.clientName}` : " do cliente"} viram
              tarefas neste projeto, em qualquer fila. Só um projeto por cliente recebe chamados:
              marcar aqui tira a marca do anterior. Chamados já abertos continuam onde estão.
            </p>
          </div>
        </div>
      </Modal.Body>

      {/* ── Footer ── */}
      <Modal.Footer className="border-t border-gray-100 pt-3 dark:border-gray-700">
        <div className="flex gap-3">
          <button
            type="button"
            onClick={handleClose}
            className="flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-gray-200 px-5 py-2.5 text-sm font-semibold text-gray-700 transition-all hover:bg-gray-50 active:scale-[.98] dark:border-gray-600 dark:text-gray-200 dark:hover:bg-navy-700"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleUpdateProject}
            disabled={!isFormValid || loading}
            className="flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-ink-900 px-5 py-2.5 text-sm font-bold text-white shadow-dv-md transition-all hover:bg-ink-800 active:scale-[.98] disabled:cursor-not-allowed disabled:opacity-50 dark:bg-white dark:text-ink-900 dark:hover:bg-gray-100"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Processando…
              </span>
            ) : (
              "Salvar alterações"
            )}
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

// ─── Sub-component ─────────────────────────────────────────────────────────────

interface ChannelCheckboxProps {
  checked: boolean;
  onChange: (value: boolean) => void;
  disabled: boolean;
  label: string;
  icon: React.ReactNode;
}

const ChannelCheckbox: React.FC<ChannelCheckboxProps> = ({
  checked,
  onChange,
  disabled,
  label,
  icon,
}) => (
  <label
    className={`flex items-center gap-2.5 rounded-xl border px-4 py-2.5 text-sm font-semibold transition-all ${
      disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"
    } ${
      checked
        ? "border-dv-blue-500 bg-dv-blue-500/10 text-dv-blue-600 dark:text-dv-cyan-300"
        : "border-gray-200 text-gray-700 hover:border-gray-300 dark:border-gray-600 dark:text-gray-300"
    }`}
  >
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => !disabled && onChange(e.target.checked)}
      disabled={disabled}
      className="h-4 w-4 rounded accent-dv-blue-500"
    />
    {icon}
    {label}
  </label>
);

export default ModalViewDetailProject;
